import {
    ShowBase,
    ReferenceField,
    ReferenceManyField,
    useShowContext,
    TabbedShowLayout,
    ListBase,
} from 'react-admin';
import { Box, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import { MemberAvatar } from './MemberAvatar';
import { MemberAside } from './MemberAside';
import { NotesLayout } from '../../notes';
import { getFullName } from '@domain/member';
import { MissionListLayout } from '@app/mission/list/MissionListLayout';
import { AntennaLightCard } from '@app/antenna/show/AntennaLightCard';
import { useConfigurationContext } from '@app/root/ConfigurationContext';
import { findMemberTypeLabel } from '../findMemberTypeLabel';
import { MemberActivityLog } from '@app/activity/MemberActivityLog';
import { LabelValueRow } from '@app/misc/LabelValueRow';
import { findVolunteerMissionAffinityLabel } from '@app/member/findVolunteerMissionAffinityLabel';
import { MemberSummary } from '@wwf/sdk';
import { MediaIcon } from '@app/media/MediaIcon';
import { MediaGridLayout } from '@app/media/MediaGridLayout';

export const MemberShow = () => (
    <ShowBase resource="member">
        <MemberShowContent />
    </ShowBase>
);

const MemberShowContent = () => {
    const { record, isPending } = useShowContext<MemberSummary>();
    if (isPending || !record) return null;

    return (
        <Grid container spacing={2} mt={1} rowGap={4}>
            <Grid item xs={12} md={9}>
                <Stack
                    flex="1"
                    flexDirection="column"
                    justifyContent="flex-start"
                    gap={2}
                >
                    <InfoCard record={record} />
                    <TabCard record={record} />
                </Stack>
            </Grid>
            <Grid item xs={12} md={3}>
                <MemberAside />
            </Grid>
        </Grid>
    );
};

const InfoCard = ({ record }: { record: MemberSummary }) => {
    const { memberTypeLabels, volunteerMissionAffinityLabels } =
        useConfigurationContext();
    return (
        <Box>
            <Card>
                <CardContent>
                    <Box display="flex">
                        <MemberAvatar variant="xl" />
                        <Box ml={2} flex="1">
                            <Typography variant="h5">
                                {getFullName(record)}
                            </Typography>
                            <Stack direction="row" gap={1} alignItems="center">
                                <Typography variant="body2" component="div">
                                    {findMemberTypeLabel(
                                        memberTypeLabels,
                                        record.type
                                    )}
                                    {record.antennas != null && ' at '}
                                </Typography>
                                {record.antennas?.map(antenna => (
                                    <ReferenceField
                                        source="id"
                                        reference="antenna"
                                        key={antenna.id}
                                        record={antenna}
                                    >
                                        <AntennaLightCard />
                                    </ReferenceField>
                                ))}
                            </Stack>

                            {record.type === 'VOLUNTEER' && (
                                <Box mr={5}>
                                    <LabelValueRow
                                        label="Mission affinity"
                                        value={
                                            record.missionAffinity
                                                ?.map(
                                                    missionAffinity =>
                                                        findVolunteerMissionAffinityLabel(
                                                            volunteerMissionAffinityLabels,
                                                            missionAffinity as (typeof volunteerMissionAffinityLabels)[number]['value']
                                                        )?.label
                                                )
                                                ?.join(', ') || 'None'
                                        }
                                    />
                                </Box>
                            )}

                            <LabelValueRow label="Job" value={record.job} />
                            <LabelValueRow
                                label="Birth Date"
                                value={record.birthDate}
                            />
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

const TabCard = ({ record }: { record: MemberSummary }) => {
    return (
        <Card>
            <CardContent>
                <TabbedShowLayout
                    sx={{
                        '& .RaTabbedShowLayout-content': { p: 0 },
                    }}
                >
                    <TabbedShowLayout.Tab label="Activity">
                        <MemberActivityLog
                            memberId={record.id}
                            context="member"
                        />
                    </TabbedShowLayout.Tab>
                    <TabbedShowLayout.Tab
                        label={
                            !record.nbMissions
                                ? 'No Missions'
                                : record.nbMissions === 1
                                  ? '1 Mission'
                                  : `${record.nbMissions} Missions`
                        }
                        path="mission"
                    >
                        <ListBase
                            resource="mission"
                            filter={{ memberId: record.id }}
                            sort={{ field: 'updatedAt', order: 'DESC' }}
                        >
                            <MissionListLayout
                                antennaId={record.antennas?.[0]?.id}
                                memberId={record.id}
                                disableSaveQuery
                            />
                        </ListBase>
                    </TabbedShowLayout.Tab>
                    <TabbedShowLayout.Tab
                        label={
                            !record.nbNotes
                                ? 'No Notes'
                                : record.nbNotes === 1
                                  ? '1 Note'
                                  : `${record.nbNotes} Notes`
                        }
                        path="notes"
                    >
                        <ReferenceManyField
                            target="memberId"
                            reference="memberNote"
                            sort={{ field: 'createdAt', order: 'DESC' }}
                        >
                            <NotesLayout reference="member" />
                        </ReferenceManyField>
                    </TabbedShowLayout.Tab>
                    <TabbedShowLayout.Tab
                        label={
                            <Stack
                                direction="row"
                                alignItems="center"
                                gap={0.5}
                            >
                                {!record.nbMedias
                                    ? 'No Medias'
                                    : record.nbMedias === 1
                                      ? '1 Media'
                                      : `${record.nbMedias} Medias`}
                                <MediaIcon />
                            </Stack>
                        }
                        path="medias"
                    >
                        <ReferenceManyField
                            target="authorId"
                            reference="memberMedia"
                            sort={{ field: 'createdAt', order: 'DESC' }}
                        >
                            <MediaGridLayout />
                        </ReferenceManyField>
                    </TabbedShowLayout.Tab>
                </TabbedShowLayout>
            </CardContent>
        </Card>
    );
};
